import { useEffect, useRef, useState } from "react";
import { Check, Loader2, MapPin, Search, X } from "lucide-react";
import "./DeliveryLocationPicker.scss";
import { isValidDeliveryLocation } from "../../features/orderUtils";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_CALLBACK_NAME = "__initDeliveryLocationGoogleMaps";
// Kam sa mapa pozrie, kym o mieste nic nevieme -- a cim sa uprednostnuju
// vysledky hladania, ked prehliadac polohu nedal. Kosice, nie Bratislava:
// prevadzky su na vychode a spendlik uprostred Bratislavy znamenal, ze clovek
// musel cez pol Slovenska odskrolovat alebo adresu najprv vyhladat.
const DEFAULT_CENTER = { lat: 48.7164, lng: 21.2611 };
const GEOCODE_BIAS_METERS = 40000; // ~40km okruh použitý na uprednostnenie výsledkov blízko používateľa
let googleMapsPromise;
let userPositionPromise;

function getUserPosition() {
  if (userPositionPromise) return userPositionPromise;

  userPositionPromise = new Promise(resolve => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      position => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => resolve(null),
      { timeout: 5000, maximumAge: 10 * 60 * 1000 }
    );
  });

  return userPositionPromise;
}

function loadGoogleMaps() {
  if (window.google?.maps?.places) return Promise.resolve(window.google.maps);
  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = new Promise((resolve, reject) => {
    window[GOOGLE_MAPS_CALLBACK_NAME] = () => resolve(window.google.maps);

    const script = document.createElement("script");
    const params = new URLSearchParams({
      key: GOOGLE_MAPS_API_KEY || "",
      libraries: "places",
      callback: GOOGLE_MAPS_CALLBACK_NAME,
      loading: "async",
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.onerror = () => reject(new Error("Google Maps sa nepodarilo načítať."));
    document.head.appendChild(script);
  });

  return googleMapsPromise;
}

function getCoordinate(latLngAccessor) {
  return typeof latLngAccessor === "function" ? latLngAccessor() : Number(latLngAccessor);
}

function getPlaceLabel(place) {
  const name = typeof place.displayName === "string" ? place.displayName : place.displayName?.text;
  return place.formattedAddress || name || "";
}

async function searchPlaces(query, limit, biasCenter) {
  const maps = await loadGoogleMaps();
  const { Place } = await maps.importLibrary("places");

  const request = {
    textQuery: query,
    fields: ["displayName", "formattedAddress", "location"],
    maxResultCount: limit,
    region: "sk",
  };
  if (biasCenter) {
    request.locationBias = { center: { lat: biasCenter.lat, lng: biasCenter.lng }, radius: GEOCODE_BIAS_METERS };
  }

  const { places } = await Place.searchByText(request);
  return (places || [])
    .map(place => ({
      label: getPlaceLabel(place),
      lat: getCoordinate(place.location?.lat),
      lng: getCoordinate(place.location?.lng),
    }))
    .filter(result => Number.isFinite(result.lat) && Number.isFinite(result.lng));
}

function getAddressText(address) {
  return String(address || "").trim();
}

function DeliveryLocationPicker({ isOpen, address, location, onConfirm, onClose }) {
  const mapElementRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const searchContainerRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const searchAbortRef = useRef(null);
  const [selectedLocation, setSelectedLocation] = useState(location || null);
  const [isLoading, setIsLoading] = useState(false);
  const [mapError, setMapError] = useState("");
  const [searchQuery, setSearchQuery] = useState(getAddressText(address));
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setSelectedLocation(location || null);
      setSearchQuery(getAddressText(address));
      setSearchResults([]);
      setSearchError("");
    }
  }, [isOpen, location, address]);

  useEffect(() => {
    if (!isOpen) return undefined;

    function handleClickOutside(event) {
      if (!searchContainerRef.current?.contains(event.target)) {
        clearTimeout(searchDebounceRef.current);
        searchAbortRef.current?.abort();
        setIsSearching(false);
        setSearchResults([]);
        setSearchError("");
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => () => {
    clearTimeout(searchDebounceRef.current);
    searchAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    if (!isOpen || !mapElementRef.current) return undefined;
    let cancelled = false;
    let resizeHandler;

    async function initialiseMap() {
      setIsLoading(true);
      setMapError("");
      try {
        const maps = await loadGoogleMaps();
        if (cancelled || mapRef.current) return;

        const startingLocation = isValidDeliveryLocation(location)
          ? { lat: Number(location.lat), lng: Number(location.lng) }
          : DEFAULT_CENTER;
        const map = new maps.Map(mapElementRef.current, {
          center: startingLocation,
          zoom: isValidDeliveryLocation(location) ? 18 : 13,
          mapTypeId: maps.MapTypeId.HYBRID,
          streetViewControl: false,
          mapTypeControl: true,
          mapTypeControlOptions: {
            style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: maps.ControlPosition.TOP_RIGHT,
            // HYBRID (nie SATELLITE) – zobrazuje aj názvy ulíc/miest nad satelitnou snímkou, ovládací prvok ho stále popíše ako "Satelit"
            mapTypeIds: [maps.MapTypeId.ROADMAP, maps.MapTypeId.HYBRID],
          },
          fullscreenControl: false,
        });

        const marker = new maps.Marker({ position: startingLocation, map, draggable: true });
        marker.addListener("dragend", () => {
          const next = marker.getPosition();
          setSelectedLocation({ lat: next.lat(), lng: next.lng() });
        });
        map.addListener("click", event => {
          marker.setPosition(event.latLng);
          setSelectedLocation({ lat: event.latLng.lat(), lng: event.latLng.lng() });
        });
        mapRef.current = map;
        markerRef.current = marker;
        const invalidateMapSize = () => maps.event.trigger(map, "resize");
        resizeHandler = invalidateMapSize;
        window.addEventListener("resize", invalidateMapSize);
        requestAnimationFrame(invalidateMapSize);

        if (!isValidDeliveryLocation(location) && getAddressText(address)) {
          const biasCenter = (await getUserPosition()) || DEFAULT_CENTER;
          const results = await searchPlaces(getAddressText(address), 1, biasCenter);
          if (!cancelled && results[0]) {
            const geocoded = { lat: results[0].lat, lng: results[0].lng };
            map.setCenter(geocoded);
            map.setZoom(18);
            marker.setPosition(geocoded);
            setSelectedLocation(geocoded);
          }
        }
      } catch (error) {
        if (!cancelled) setMapError("Mapu alebo adresu sa nepodarilo načítať. Miesto môžete označiť ručne.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    initialiseMap();
    return () => {
      cancelled = true;
      if (resizeHandler) window.removeEventListener("resize", resizeHandler);
    };
  }, [isOpen]);

  async function searchAddress(query = searchQuery) {
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 3) return;

    // Place.searchByText nepodporuje AbortSignal – controller tu iba chráni pred aplikovaním zastaranej odpovede.
    searchAbortRef.current?.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;
    setIsSearching(true);
    setSearchError("");

    try {
      const biasCenter = (await getUserPosition()) || DEFAULT_CENTER;
      if (controller.signal.aborted) return;
      const results = await searchPlaces(normalizedQuery, 5, biasCenter);
      if (controller.signal.aborted) return;
      if (results.length === 0) {
        setSearchError("Zadané miesto sa nenašlo. Skúste upresniť názov ulice alebo mesta.");
      } else {
        setSearchResults(results);
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      setSearchError("Vyhľadávanie adresy sa nepodarilo dokončiť. Skúste to znova.");
    } finally {
      if (!controller.signal.aborted) setIsSearching(false);
    }
  }

  function handleSearchChange(event) {
    const nextQuery = event.target.value;
    setSearchQuery(nextQuery);
    setSearchResults([]);
    setSearchError("");
    clearTimeout(searchDebounceRef.current);
    searchAbortRef.current?.abort();

    if (nextQuery.trim().length < 3) {
      setIsSearching(false);
      return;
    }

    searchDebounceRef.current = setTimeout(() => searchAddress(nextQuery), 450);
  }

  function selectSearchResult(result) {
    const nextLocation = { lat: result.lat, lng: result.lng };
    clearTimeout(searchDebounceRef.current);
    searchAbortRef.current?.abort();
    setIsSearching(false);
    setSelectedLocation(nextLocation);
    setSearchQuery(result.label || "");
    setSearchResults([]);
    setSearchError("");
    markerRef.current?.setPosition(nextLocation);
    mapRef.current?.panTo(nextLocation);
    mapRef.current?.setZoom(17);
  }

  useEffect(() => {
    if (!isOpen || !mapRef.current) return;
    const map = mapRef.current;
    setTimeout(() => window.google?.maps?.event.trigger(map, "resize"), 0);
  }, [isOpen]);

  function closeMap() {
    clearTimeout(searchDebounceRef.current);
    searchAbortRef.current?.abort();
    if (window.google?.maps) {
      if (mapRef.current) window.google.maps.event.clearInstanceListeners(mapRef.current);
      if (markerRef.current) {
        window.google.maps.event.clearInstanceListeners(markerRef.current);
        markerRef.current.setMap(null);
      }
    }
    mapRef.current = null;
    markerRef.current = null;
    onClose();
  }

  if (!isOpen) return null;

  return (
    <div className="delivery-location__overlay" role="dialog" aria-modal="true" aria-label="Spresnenie doručovacej polohy">
      <div className="delivery-location__modal">
        <div className="delivery-location__header">
          <div>
            <h2 className="delivery-location__title">Spresniť polohu doručenia</h2>
            <form className="delivery-location__search" onSubmit={event => { event.preventDefault(); searchAddress(); }} ref={searchContainerRef}>
              <Search size={17} className="delivery-location__search-icon" />
              <input
                type="search"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Hľadať adresu alebo miesto..."
                aria-label="Hľadať adresu alebo miesto"
              />
              <button type="submit" aria-label="Vyhľadať adresu" disabled={isSearching || !searchQuery.trim()}>
                {isSearching ? <Loader2 size={17} className="delivery-location__spin" /> : <Search size={17} />}
              </button>
              {(isSearching || searchResults.length > 0 || searchError) && (
                <div className="delivery-location__results">
                  {isSearching && (
                    <div className="delivery-location__results-status">
                      <Loader2 size={16} className="delivery-location__spin" />
                      <span>Hľadám adresy...</span>
                    </div>
                  )}
                  {!isSearching && searchResults.map((result, index) => (
                      <button type="button" key={`${result.lat}-${result.lng}-${index}`} onClick={() => selectSearchResult(result)}>
                        <MapPin size={15} />
                        <span>{result.label}</span>
                      </button>
                    ))}
                  {!isSearching && searchError && <div className="delivery-location__results-status">{searchError}</div>}
                </div>
              )}
            </form>
            {searchError && <p className="delivery-location__search-error">{searchError}</p>}
          </div>
          <button type="button" className="delivery-location__close" onClick={closeMap} aria-label="Zavrieť mapu">
            <X size={20} />
          </button>
        </div>
        <div className="delivery-location__map-wrap">
          <div className="delivery-location__banner">
            <MapPin size={17} />
            <span>Presuňte špendlík presne na miesto doručenia (brána, vchod, rampa).</span>
          </div>
          <div ref={mapElementRef} className="delivery-location__map" />
          {isLoading && (
            <div className="delivery-location__loading"><Loader2 size={18} className="delivery-location__spin" /> Načítavam mapu a adresu…</div>
          )}
          {mapError && <div className="delivery-location__error">{mapError}</div>}
        </div>
        <div className="delivery-location__footer">
          <button type="button" className="delivery-location__cancel" onClick={closeMap}>Zrušiť</button>
          <button
            type="button"
            className="delivery-location__confirm"
            // Druhy argument je text adresy, ktoru si clovek vybral z ponuky.
            // Obsluha si adresu pise sama a ignoruje ho; zakaznik ju volnym
            // textom zadavat nesmie (R23), takze si ju berie prave odtialto.
            onClick={() => { onConfirm(selectedLocation, searchQuery.trim()); closeMap(); }}
            disabled={!isValidDeliveryLocation(selectedLocation)}
          >
            <Check size={17} /> Potvrdiť polohu
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeliveryLocationPicker;
